import React from 'react';
import { ArrowRightIcon, CheckIcon, CalendarIcon, UsersIcon, MessageCircleIcon, ZapIcon, UserIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function Coaching() {
  const {
    t
  } = useLanguage();
  const signs = t('coaching.signs') as string[];
  const whatWeDoItems = t('coaching.whatWeDoItems') as string[];
  const relatedChallenges = [{
    icon: <UsersIcon size={22} className="text-coral" data-id="element-1275" />,
    titleKey: 'coaching.rel1Title',
    descKey: 'coaching.rel1Desc',
    path: '/services/new-to-leadership',
    color: 'border-coral/20'
  }, {
    icon: <MessageCircleIcon size={22} className="text-teal" data-id="element-1276" />,
    titleKey: 'coaching.rel2Title',
    descKey: 'coaching.rel2Desc',
    path: '/services/conflict-and-conversations',
    color: 'border-teal/20'
  }, {
    icon: <ZapIcon size={22} className="text-blue-600" data-id="element-1277" />,
    titleKey: 'coaching.rel3Title',
    descKey: 'coaching.rel3Desc',
    path: '/services/leading-in-complexity',
    color: 'border-blue-200'
  }];
  return <PageTransition data-id="element-1278">
      <div className="pt-12 pb-24" data-id="element-1279">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1280">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1281" />
          <Animate className="max-w-3xl" data-id="element-1282">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1283">
              {t('coaching.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1284">
              {t('coaching.title')}
              <br data-id="element-1285" />
              <span className="text-coral" data-id="element-1286">{t('coaching.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1287">
              {t('coaching.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1288">
              {t('coaching.intro2')}
            </p>
            <Link to="/contact" data-id="element-1289">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1291" />} data-id="element-1290">
                {t('coaching.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs section */}
        <section className="bg-cream py-20 mb-20" data-id="element-1292">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1293">
            <Animate className="mb-12" data-id="element-1294">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1295">
                {t('coaching.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1296">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1297">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1298">
                    <div className="mt-0.5 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1299">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1300" />
                    </div>
                    <span className="text-gray-700" data-id="element-1301">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1302">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1303">
            <Animate data-id="element-1304">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1305">
                {t('coaching.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1306">
                {t('coaching.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1307">
                {t('coaching.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1308">
              <div className="space-y-3" data-id="element-1309">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1310">
                    <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1311">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1312" />
                    </div>
                    <span className="text-gray-700" data-id="element-1313">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* What it typically covers */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1314">
          <Animate className="mb-10" data-id="element-1315">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1316">
              {t('coaching.relatedTitle')}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1317">{t('coaching.relatedSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1318">
            {relatedChallenges.map((ch, i) => <Animate key={i} delay={i * 0.1} data-id="element-1319">
                <Link to={ch.path} className="block group h-full" data-id="element-1320">
                  <Card className={`h-full p-6 border-2 ${ch.color} hover:shadow-md transition-all`} hoverEffect data-id="element-1321">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1322">
                      {ch.icon}
                      <h3 className="font-bold text-charcoal" data-id="element-1323">{t(ch.titleKey)}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4" data-id="element-1324">{t(ch.descKey)}</p>
                    <div className="flex items-center text-sm font-bold text-coral" data-id="element-1325">
                      {t('coaching.learnMore')}
                      <ArrowRightIcon size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1326" />
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1327">
          <Animate data-id="element-1328">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-1329">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1330">
                <CalendarIcon size={32} className="text-coral" data-id="element-1331" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1332">{t('coaching.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1333">{t('coaching.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1334">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1336" />} data-id="element-1335">
                  {t('coaching.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}